import { CollectionTray } from '../../../domain/collection-tray';
import { CarouselPageItemApiModel, CreateCarouselPageItemRequestApiModel, GenericLayoutApiModel } from '@cxb/editor-stb-api';
import { mapperPipe, PlatformType, remainderMapper } from 'osn-shared';
import {
  platformTargetingMapper,
  scheduledDateMapperDomainToApi,
  targetingMapperDomainToApi,
} from '../../mapping/create-tray-domain-to-api';
import { commonMapper, mapApiModelImageWithPlatformToDomain } from '../../mapping/create-tray-api-to-domain';
import { CollectionTrayFormViewModel } from './collection-tray-form-view-model';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';
import {
  mapDomainImagesToPlatformLocalizedViewModelImage,
  platformMapper,
  scheduledDateMapperDomainToViewModel,
  targetingMapper,
} from '../create-tray-screen/mapping/create-tray-domain-to-vm';
import { mapViewModelImagesToDomainImages } from '../create-tray-screen/mapping/create-tray-vm-to-domain';

// 'CollectionTray'

/**
 * DOMAIN -> API
 */

export const mapDomainCollectionTrayToApi = (
  tray: CollectionTray,
  pageVersionId: string,
  tenantId?: string
): CreateCarouselPageItemRequestApiModel => {
  return remainderMapper(mapperPipe(tray, targetingMapperDomainToApi, scheduledDateMapperDomainToApi, platformTargetingMapper), () => ({
    pageVersionId,
    backgroundImages: tray.backgroundImages.map(({ id, platformId }) => ({ imageId: id, platformId })),
    feedId: tray.feedId,
    layout: GenericLayoutApiModel.Collection,
    nameLocalized: tray.title,
    descriptionLocalized: tray.description,
    collectionPageId: tray.collectionPageTargetId,
    tenantId,
  }));
};

/**
 *  API -> DOMAIN
 */

export const mapApiCollectionTrayToDomain = (pageItem: CarouselPageItemApiModel): CollectionTray => {
  return remainderMapper(mapperPipe(pageItem, commonMapper), () => ({
    backgroundImages: pageItem.backgroundImages.map(mapApiModelImageWithPlatformToDomain),
    countryIds: pageItem.countryIds,
    description: pageItem.descriptionLocalized,
    feedId: pageItem.feedId,
    scheduledFrom: Number(pageItem.scheduledFrom),
    scheduledTo: Number(pageItem.scheduledTo),
    title: pageItem.nameLocalized,
    type: 'CollectionTray',
    userSegmentIds: pageItem.userSegmentIds,
    collectionPageTargetId: pageItem.collectionPageId,
  }));
};

/**
 * DOMAIN -> VIEWMODEL
 */

export const mapCollectionTrayDomainModelToViewModel = (pageItem: CollectionTray): CollectionTrayFormViewModel => {
  const commonProperties = mapperPipe(pageItem, commonMapper, targetingMapper, scheduledDateMapperDomainToViewModel, platformMapper);

  return remainderMapper(commonProperties, () => ({
    backgroundImages: mapDomainImagesToPlatformLocalizedViewModelImage(pageItem.backgroundImages),
    description: pageItem.description,
    feedId: pageItem.feedId,
    title: pageItem.title,
    type: TrayFormViewModelType.CollectionTray,
    collectionPageTargetId: pageItem.collectionPageTargetId,
  }));
};

/**
 * VIEWMODEL -> DOMAIN
 */

export const mapCollectionTrayViewModelToDomainModel = (
  viewModel: CollectionTrayFormViewModel,
  platformIds: Record<PlatformType, string>
): CollectionTray => {
  return {
    backgroundImages: mapViewModelImagesToDomainImages(viewModel.backgroundImages, platformIds),
    countryIds: viewModel.countryIds,
    platformIds: viewModel.platformIds,
    description: viewModel.description,
    draggable: false,
    feedId: viewModel.feedId,
    id: viewModel.id,
    order: undefined,
    scheduledFrom: viewModel.scheduling?.scheduledFrom ? new Date(viewModel.scheduling.scheduledFrom).getTime() : undefined,
    scheduledTo: viewModel.scheduling?.scheduledTo ? new Date(viewModel.scheduling.scheduledTo).getTime() : undefined,
    title: viewModel.title,
    type: 'CollectionTray',
    userSegmentIds: viewModel.userSegmentIds,
    visible: undefined,
    collectionPageTargetId: viewModel.collectionPageTargetId,
  };
};
